import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import comingSoonImage from "@/assets/4FACEMEDIA_Projekt_Coming_Soon.jpeg";

interface Slide {
  title: string;
  subtitle: string;
  category: string;
  number: string;
  gradient: string;
  videoId?: string;
  videoSrc?: string;
  image?: string;
}

const slides: Slide[] = [
  {
    title: "Porsche Carrera Cup Italia Imola",
    subtitle: "Wir begleiten Lirim Zendeli beim Rennwochenende, Porsche Carrera Cup Italia.",
    category: "Video",
    number: "01",
    gradient: "from-primary/30 via-background/60 to-deep-blue/40",
    videoSrc: "/videos/porsche-carrera-cup-italia-2025.mp4",
  },
  {
    title: "​Grasbahnrennen Lüdinghausen",
    subtitle: "",
    category: "Video",
    number: "02",
    gradient: "from-deep-blue/40 via-background/60 to-primary/30",
    videoId: "AbWCGUExRuk",
  },
  {
    title: "VfL SparkassenStars Bochum",
    subtitle: "Emotionale Eventbegleitung vom ersten Moment bis zum letzten Applaus.",
    category: "Event",
    number: "03",
    gradient: "from-deep-purple/40 via-background/60 to-primary/20",
    videoId: "WiUFwseMoP4",
  },
  {
    title: "Papageienpark Bochum ",
    subtitle: "Mit einer Kampagne in 90 Tagen 6,7 Millionen Views sowie mehr als 26.000 neue Follower generiert. ",
    category: "Social Media",
    number: "04",
    gradient: "from-primary/20 via-deep-purple/40 to-deep-blue/30",
    videoId: "mfNjW0H2lG4",
  },
];

const VideoSlider = () => {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState<"next" | "prev">("next");

  const goTo = useCallback(
    (index: number, dir: "next" | "prev" = "next") => {
      if (isTransitioning) return;
      setDirection(dir);
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 900);
    },
    [isTransitioning]
  );

  const next = useCallback(() => goTo((current + 1) % slides.length, "next"), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + slides.length) % slides.length, "prev"), [current, goTo]);

  useEffect(() => {
    const duration = 9000;
    const timer = setTimeout(next, duration);
    return () => clearTimeout(timer);
  }, [next, current]);

  const slide = slides[current];

  return (
    <section className="relative w-full h-screen overflow-hidden">
      {/* Cinematic gradient background */}
      {/* Video or gradient background */}
      {slide.videoSrc ? (
        <video
          src={slide.videoSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
      ) : slide.videoId ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${slide.videoId}?autoplay=1&mute=1&loop=1&playlist=${slide.videoId}&controls=0&showinfo=0&rel=0&modestbranding=1`}
          title={slide.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none scale-[1.2]"
          style={{ border: 0 }}
        />
      ) : slide.image ? (
        <img
          src={slide.image}
          alt={slide.title}
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div
          className={`absolute inset-0 bg-gradient-to-br ${slide.gradient} transition-all duration-1000 ease-out`}
        />
      )}
      {/* Overlay for text readability */}
      <div className="absolute inset-0 bg-background/40" />

      {/* Film grain overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />

      {/* Ambient light effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute w-[600px] h-[600px] rounded-full bg-primary/8 blur-[120px]"
          style={{
            top: "10%",
            left: `${20 + current * 15}%`,
            transition: "left 2s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full bg-glow/6 blur-[100px]"
          style={{
            bottom: "5%",
            right: `${10 + current * 10}%`,
            transition: "right 2s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        />
      </div>

      {/* Slide number — cinematic large watermark */}
      <div className="absolute top-1/2 right-12 -translate-y-1/2 z-10 hidden lg:block">
        <span
          className="text-[12rem] font-bold leading-none text-foreground/[0.03] transition-all duration-700 block"
          key={`num-${current}`}
        >
          {slide.number}
        </span>
      </div>


      {/* Content — bottom left corner */}
      <div className="absolute bottom-6 left-6 lg:bottom-10 lg:left-10 z-10 max-w-2xl">
        <div
          className={`transition-all duration-700 ease-out ${
            isTransitioning
              ? direction === "next"
                ? "opacity-0 translate-y-4"
                : "opacity-0 -translate-y-4"
              : "opacity-100 translate-y-0"
          }`}
        >
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-2 leading-tight whitespace-pre-line">
            {slide.title}
          </h2>


        </div>
      </div>

      {/* Navigation — minimal bottom right */}
      <div className="absolute bottom-24 lg:bottom-32 right-6 lg:right-12 z-20 flex items-center gap-4">
        <button
          onClick={prev}
          className="w-12 h-12 rounded-full border border-foreground/20 flex items-center justify-center text-foreground/60 hover:text-foreground hover:border-foreground/40 transition-all duration-300"
          aria-label="Vorheriges Projekt"
        >
          <ChevronLeft size={20} />
        </button>
        <span className="text-sm text-foreground/40 font-light tabular-nums min-w-[3rem] text-center">
          {slide.number} / {String(slides.length).padStart(2, "0")}
        </span>
        <button
          onClick={next}
          className="w-12 h-12 rounded-full border border-foreground/20 flex items-center justify-center text-foreground/60 hover:text-foreground hover:border-foreground/40 transition-all duration-300"
          aria-label="Nächstes Projekt"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 h-[2px] bg-foreground/5">
        <div
          className="h-full bg-primary/60 transition-all duration-500 ease-out"
          style={{ width: `${((current + 1) / slides.length) * 100}%` }}
        />
      </div>
    </section>
  );
};

export default VideoSlider;
